<template>
  <div class="viewer">
    <h2>📚 Formations</h2>
    <div v-for="f in formationsSorted" :key="'f_' + f.numero" class="formation">
      <div
        class="row formation-row"
        :class="{ viewed: isFormationFullyViewed(f) }"
        role="button"
        tabindex="0"
        @click="toggleFormation(f.numero)"
      >
        <span class="arrow" :class="{ open: isFormationOpen(f.numero) }"
          >▸</span
        >
        <span class="title">{{ f.name }}</span>
        <span class="badge" :class="{ success: isFormationFullyViewed(f) }">
          {{ viewedCountFormation(f) }} / {{ lessonsCountFormation(f) }}
        </span>
      </div>

      <div v-if="isFormationOpen(f.numero)" class="formation-body">
        <div
          v-for="c in sortedChapters(f)"
          :key="'f_' + f.numero + '_c_' + c.numero"
          class="chapter"
        >
          <div
            class="row chapter-row"
            :class="{ viewed: isChapterFullyViewed(c) }"
            role="button"
            tabindex="0"
            @click="toggleChapter(f.numero, c.numero)"
          >
            <span
              class="arrow"
              :class="{ open: isChapterOpen(f.numero, c.numero) }"
              >▸</span
            >
            <span class="title">{{ c.numero }}. {{ c.name }}</span>
            <span class="badge" :class="{ success: isChapterFullyViewed(c) }">
              {{ viewedCountChapter(c) }} / {{ lessonsCountChapter(c) }}
            </span>
          </div>

          <div v-if="isChapterOpen(f.numero, c.numero)" class="chapter-body">
            <div
              v-for="l in sortedLessons(c)"
              :key="'f_' + f.numero + '_c_' + c.numero + '_l_' + l.lessonUrl"
              class="lesson"
            >
              <div
                class="lesson-box"
                :class="{
                  viewed: isViewed(l.lessonUrl),
                  open: isLessonOpen(f.numero, c.numero, l.lessonUrl),
                }"
              >
                <div
                  class="row lesson-row row--no-border"
                  :class="{ viewed: isViewed(l.lessonUrl) }"
                  role="button"
                  tabindex="0"
                  @click="toggleLesson(f.numero, c.numero, l.lessonUrl)"
                >
                  <span
                    class="arrow"
                    :class="{
                      open: isLessonOpen(f.numero, c.numero, l.lessonUrl),
                    }"
                    >▸</span
                  >
                  <span class="title">
                    {{ l.name || l.lessonUrl }}
                  </span>
                  <button
                    class="mark"
                    :class="isViewed(l.lessonUrl) ? 'mark-viewed' : ''"
                    @click.stop="toggleViewed(l.lessonUrl)"
                  >
                    <img src="../static/img/SeenIcon.png" />
                  </button>
                </div>

                <div
                  v-if="isLessonOpen(f.numero, c.numero, l.lessonUrl)"
                  class="videos"
                >
                  <div v-for="v in l.videos" :key="v.id" class="video">
                    <video
                      :src="videoSrc(v)"
                      controls
                      preload="metadata"
                    ></video>
                    <div v-if="v.sumup" class="sumup">
                      {{ v.sumup }}
                    </div>
                  </div>
                </div>
              </div>
              <!-- /lesson-box -->
            </div>
            <!-- /lesson -->
          </div>
          <!-- /chapter-body -->
        </div>
        <!-- /chapter -->
      </div>
      <!-- /formation-body -->
    </div>
    <!-- /formation -->
  </div>
</template>

<script>
import axios from "axios";
import api from "../api/api";
import { onRefresh, offRefresh } from "../utils/refresh";

export default {
  data() {
    return {
      formationsRaw: [],
      viewed: [],
      openFormations: {}, // { [fNum]: true }
      openChapterByFormation: {}, // { [fNum]: cNum | null }
      openLessonByChapter: {}, // { [chapterKey]: lessonUrl | null }  -> exclusif
    };
  },

  async created() {
    const [fRes, vRes] = await Promise.all([
      axios.get(this.apiUrl + "/formations"),
      axios.get(this.apiUrl + "/viewed"),
    ]);
    this.formationsRaw = fRes.data?.formations || [];
    this.viewed = vRes.data || [];

    onRefresh(this.handleOnRefresh);
  },
  computed: {
    apiUrl() {
      return api.api;
    },
    videosUrl() {
      return api.videos;
    },
    formationsSorted() {
      const arr = (this.formationsRaw || []).slice();
      arr.sort((a, b) => (a.numero || 0) - (b.numero || 0));
      return arr;
    },
  },

  methods: {
    async handleOnRefresh() {
      try {
        const { data } = await axios.get(this.apiUrl + "/viewed");
        this.viewed = Array.isArray(data) ? data : [];
      } catch (e) {
        // silencieux
      }
    },
    // Tri
    sortedChapters(formation) {
      const chapters = (formation.chapters || []).slice();
      chapters.sort((a, b) => (a.numero || 0) - (b.numero || 0));
      return chapters;
    },
    sortedLessons(chapter) {
      return (chapter.lessons || []).slice();
    },

    // Compteurs
    lessonsCountChapter(c) {
      return (c.lessons || []).length;
    },
    viewedCountChapter(c) {
      return (c.lessons || []).reduce(
        (acc, l) => acc + (this.isViewed(l.lessonUrl) ? 1 : 0),
        0
      );
    },
    lessonsCountFormation(f) {
      return (f.chapters || []).reduce(
        (acc, c) => acc + (c.lessons || []).length,
        0
      );
    },
    viewedCountFormation(f) {
      return (f.chapters || []).reduce(
        (acc, c) =>
          acc +
          (c.lessons || []).reduce(
            (a2, l) => a2 + (this.isViewed(l.lessonUrl) ? 1 : 0),
            0
          ),
        0
      );
    },

    // Helper clés
    chapterKey(fNum, cNum) {
      return `${fNum}::${cNum}`;
    },

    // Formations
    isFormationOpen(fNum) {
      return !!this.openFormations[fNum];
    },
    toggleFormation(fNum) {
      this.$set(this.openFormations, fNum, !this.openFormations[fNum]);
    },

    // Un seul chapitre ouvert par formation
    isChapterOpen(fNum, cNum) {
      return this.openChapterByFormation[fNum] === cNum;
    },
    toggleChapter(fNum, cNum) {
      const current = this.openChapterByFormation[fNum] || null;
      const next = current === cNum ? null : cNum;
      this.$set(this.openChapterByFormation, fNum, next);
      // en changeant de chapitre, on ferme la leçon ouverte de l'ancien chapitre
      if (next !== current) {
        const oldKey = this.chapterKey(fNum, current);
        if (current !== null && this.openLessonByChapter[oldKey]) {
          this.$set(this.openLessonByChapter, oldKey, null);
        }
      }
    },

    // Leçons — exclusif par chapitre
    isLessonOpen(fNum, cNum, url) {
      const key = this.chapterKey(fNum, cNum);
      return this.openLessonByChapter[key] === url;
    },
    toggleLesson(fNum, cNum, url) {
      const key = this.chapterKey(fNum, cNum);
      const current = this.openLessonByChapter[key] || null;
      const next = current === url ? null : url;
      this.$set(this.openLessonByChapter, key, next);
    },

    // Vidéo locale
    videoSrc(video) {
      return `${this.videosUrl}/${video.id}.mp4`;
    },

    // Marquage "vu"
    isViewed(url) {
      return this.viewed.includes(url);
    },
    async toggleViewed(url) {
      const viewed = this.isViewed(url);

      if (viewed) {
        this.viewed = this.viewed.filter((u) => u !== url);
        try {
          await axios.delete(this.apiUrl + "/viewed", {
            data: { lessonUrl: url },
          });
        } catch (e) {
          this.viewed.push(url);
        }
      } else {
        this.viewed.push(url);
        try {
          await axios.post(this.apiUrl + "/viewed", { lessonUrl: url });
        } catch (e) {
          this.viewed = this.viewed.filter((u) => u !== url);
        }
      }
    },

    // Helpers "tout vu"
    isChapterFullyViewed(chapter) {
      const lessons = chapter?.lessons || [];
      if (lessons.length === 0) return false;
      return lessons.every((l) => this.isViewed(l.lessonUrl));
    },
    isFormationFullyViewed(formation) {
      const chapters = formation?.chapters || [];
      const lessons = [].concat(...chapters.map((c) => c.lessons || []));
      if (lessons.length === 0) return false;
      return lessons.every((l) => this.isViewed(l.lessonUrl));
    },
  },
  beforeDestroy() {
    offRefresh(this.handleOnRefresh);
  },
};
</script>

<style>
:root {
  --bg: #0b1220;
  --card: #486096;
  --muted: #64748b;
  --line: #e5e7eb;
  --line-soft: #eef2ff;
  --accent: #4f46e5;
  --ok-bg: #ecfdf5;
  --ok-border: #10b981;
  --ok-text: #065f46;
}

/* anti overflow horizontal global + centrage colonne */
html,
body,
#app {
  max-width: 100%;
  overflow-x: hidden;
}
</style>

<style scoped>
.viewer {
  max-width: 900px; /* un peu plus étroit pour un effet centré propre */
  margin: 0 auto;
  padding: 16px;
  box-sizing: border-box;

  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 14px;
  box-shadow: 0 6px 20px rgba(2, 6, 23, 0.06);
}

.row {
  width: calc(100% - 32px);
  padding: 12px 14px;
  margin: 6px 0;
  display: flex;
  align-items: center;
  gap: 10px;
  background: var(--surface-2);
  border: 1px solid var(--line);
  border-radius: 10px;
  cursor: pointer;
  text-align: left;
  transition: background 120ms ease, border-color 120ms ease,
    box-shadow 120ms ease;
  flex-wrap: wrap;
  min-width: 0;
}
.row:hover {
  /* background: var(--line-soft); */
  border-color: #c7d2fe;
  /* box-shadow: 0 2px 10px rgba(59, 130, 246, 0.08); */
}
.row.viewed {
  background: var(--ok-bg);
  border-color: var(--ok-border);
}
.row.viewed .title {
  color: var(--ok-text);
  font-weight: 600;
}

/* supprime les décalages vers la droite */
.chapter-row {
  margin-left: 0;
}
.formation-body {
  margin-top: 8px;
}
.chapter-body {
  margin: 12px 0;
}
.lesson {
  margin: 8px 0;
}

/* Bloc leçon (entête + vidéos) */
.lesson-box {
  border: 1px solid var(--line);
  border-radius: 10px;
  padding: 0 14px 10px 14px;
  background: var(--surface-2);
}
.lesson-box.viewed {
  background: var(--ok-bg);
  border-color: var(--ok-border);
}
.lesson-box.open {
  box-shadow: 0 6px 22px rgba(2, 6, 23, 0.06);
}

/* Pas de double bordure dans l'entête de la leçon */
.row--no-border {
  border: none;
  background: transparent;
  box-shadow: none;
  padding: 12px 0 0 0;
}

.arrow {
  transition: transform 0.15s ease;
  display: inline-block;
  width: 1.1em;
  flex: 0 0 auto;
}
.arrow.open {
  transform: rotate(90deg);
}

.title {
  flex: 1 1 auto;
  min-width: 0;
  overflow: visible; /* plus de rognage */
  text-overflow: initial; /* plus de ... */
  white-space: normal; /* autorise les retours à la ligne */
  word-break: break-word; /* casse si super long mot/URL */
  line-height: 1.25;
}

.badge {
  font-size: 12px;
  color: var(--text);
  background: var(--surface-hover);
  padding: 4px 8px;
  border-radius: 999px;
  border: 1px solid var(--border);
  flex: 0 0 auto;
}
.badge.success {
  background: var(--ok-bg);
  border-color: var(--ok-border);
  color: var(--ok-text);
  font-weight: 600;
}

.videos {
  margin: 8px 0 0 0;
} /* plus d'indent à droite */
video {
  display: block;
  width: 100%;
  max-width: 100%;
  height: auto;
  border-radius: 10px;
  box-shadow: 0 6px 22px rgba(2, 6, 23, 0.12);
}

/* Bouton */
.mark {
  background: var(--mark-bg);
  color: var(--text);
  border: 0;
  border-radius: 8px;
  /* padding: 6px 12px; */
  cursor: pointer;
  transition: transform 80ms ease, opacity 80ms ease, background 120ms ease;
  flex: 0 0 auto;
  width: 30px;
}
.mark:hover {
  transform: translateY(-1px);
}
.mark[disabled] {
  background: #dcfce7;
  color: var(--ok-text);
  cursor: default;
}
.mark img {
  width: 100%;
  height: 100%;
  vertical-align: middle;
}

.mark-viewed {
  background: #81f2c0;
}
.mark-viewed:hover {
  background: #88ffc9;
}

.sumup {
  margin-top: 8px;
  padding: 10px 12px;
  border: 1px dashed var(--line);
  border-radius: 10px;
  background: var(--surface);
  font-size: 14px;
  line-height: 1.5;
  color: var(--text);
  white-space: pre-wrap; /* garde les retours à la ligne si présents */
  word-wrap: break-word; /* évite tout débordement horizontal */
}

.video + .video {
  margin-top: 18px; /* espace entre plusieurs vidéos d’une même leçon */
}

/* Layout en 3 colonnes pour la ligne de leçon */
.row.lesson-row {
  display: grid; /* au lieu de flex pour cette row */
  grid-template-columns: 1.4em 1fr auto; /* flèche | titre fluide | bouton */
  gap: 10px;
  align-items: start; /* bouton en haut quand le titre wrap */
  flex-wrap: unset; /* neutralise le wrap hérité */
  width: 100%;
}

/* La flèche garde une petite largeur fixe */
.row.lesson-row .arrow {
  width: 1.1em;
  flex: initial;
  margin-top: 2px; /* un léger alignement vertical */
}

/* Le titre peut s’étendre en hauteur sans pousser le bouton en dessous */
.row.lesson-row .title {
  min-width: 0;
  white-space: normal;
  overflow: visible;
  text-overflow: initial;
  word-break: break-word;
  line-height: 1.3;
}

/* Le bouton reste à droite, largeur fixe, ne rétrécit pas */
.row.lesson-row .mark {
  justify-self: end; /* colle à droite de la colonne 3 */
  align-self: start; /* en haut si le titre wrappe */
  width: 30px; /* ta largeur actuelle */
  flex: initial; /* inutile en grid mais évite les héritages */
}
</style>
